import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { PresentationSlide1, SLIDE_1_DURATION } from './PresentationSlide1';
import { PresentationSlide2, SLIDE_2_DURATION } from './PresentationSlide2';
import { PresentationSlide3, SLIDE_3_DURATION } from './PresentationSlide3';
import { PresentationSlide4, SLIDE_4_DURATION } from './PresentationSlide4';
import { PresentationSlide5, SLIDE_5_DURATION } from './PresentationSlide5';
import { PresentationSlide6, SLIDE_6_DURATION } from './PresentationSlide6';
import { PresentationSlide7, SLIDE_7_DURATION } from './PresentationSlide7';
import { PresentationSlide8, SLIDE_8_DURATION } from './PresentationSlide8';
const SLIDES = [{
  id: 'slide-1',
  label: 'The Reality',
  duration: SLIDE_1_DURATION
}, {
  id: 'slide-2',
  label: 'The Resolution',
  duration: SLIDE_2_DURATION
}, {
  id: 'slide-3',
  label: 'The Setup',
  duration: SLIDE_3_DURATION
}, {
  id: 'slide-4',
  label: 'One Number',
  duration: SLIDE_4_DURATION
}, {
  id: 'slide-5',
  label: 'Continuous Memory',
  duration: SLIDE_5_DURATION
}, {
  id: 'slide-6',
  label: 'Agentic Action',
  duration: SLIDE_6_DURATION
}, {
  id: 'slide-7',
  label: 'Flow Builder',
  duration: SLIDE_7_DURATION
}, {
  id: 'slide-8',
  label: 'The Future',
  duration: SLIDE_8_DURATION
}] as const;
const TOTAL_DURATION = SLIDES.reduce((a, s) => a + s.duration, 0);
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
export function PresentationDeck() {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  // Refs mirroring state — RAF tick reads these directly, immune to stale closures
  const playingRef = useRef<boolean>(true);
  const scrubbingRef = useRef<boolean>(false);
  useEffect(() => {
    playingRef.current = playing;
    if (playing && !scrubbing) {
      // Resume — re-anchor the start time from the last paused position
      startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    } else {
      // Pausing — capture live time
      pausedAtRef.current = Math.min(TOTAL_DURATION, Math.max(0, (Date.now() - startedAtRef.current) / 1000));
    }
  }, [playing, scrubbing]);
  useEffect(() => {
    scrubbingRef.current = scrubbing;
    if (scrubbing) {
      pausedAtRef.current = Math.min(TOTAL_DURATION, Math.max(0, (Date.now() - startedAtRef.current) / 1000));
    } else if (playingRef.current) {
      startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    }
  }, [scrubbing]);
  // Single, persistent RAF loop — checks refs each frame instead of using effect deps
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      if (playingRef.current && !scrubbingRef.current) {
        const t = (Date.now() - startedAtRef.current) / 1000;
        if (t >= TOTAL_DURATION) {
          setElapsed(TOTAL_DURATION);
          setPlaying(false);
          playingRef.current = false;
        } else {
          setElapsed(t);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  // Determine which slide is active and its local time
  const slideInfo = (() => {
    let acc = 0;
    for (let i = 0; i < SLIDES.length; i++) {
      const s = SLIDES[i];
      if (elapsed < acc + s.duration || i === SLIDES.length - 1) {
        return {
          index: i,
          localElapsed: Math.max(0, Math.min(elapsed - acc, s.duration))
        };
      }
      acc += s.duration;
    }
    return {
      index: 0,
      localElapsed: 0
    };
  })();
  const replay = () => {
    pausedAtRef.current = 0;
    // Resync RAF anchor so a running tick picks up 0 immediately
    // (without this, replay during playback gets overwritten on next frame).
    startedAtRef.current = Date.now();
    setElapsed(0);
    setPlaying(true);
  };
  const goToSlide = (i: number) => {
    const newElapsed = SLIDES.slice(0, i).reduce((a, s) => a + s.duration, 0);
    pausedAtRef.current = newElapsed;
    // Resync RAF anchor so the running tick picks up the new position immediately
    startedAtRef.current = Date.now() - newElapsed * 1000;
    setElapsed(newElapsed);
    if (!playing) {
      setPlaying(true);
    }
  };
  const setElapsedFromClientX = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newElapsed = ratio * TOTAL_DURATION;
    pausedAtRef.current = newElapsed;
    startedAtRef.current = Date.now() - newElapsed * 1000;
    setElapsed(newElapsed);
  }, []);
  // Scrub interactions
  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setScrubbing(true);
    setElapsedFromClientX(e.clientX);
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!scrubbing) return;
    setElapsedFromClientX(e.clientX);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!scrubbing) return;
    setScrubbing(false);
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };
  // Keyboard controls (space = play/pause, arrows = scrub)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setPlaying(p => !p);
      } else if (e.code === 'ArrowLeft') {
        const ne = Math.max(0, elapsed - 2);
        pausedAtRef.current = ne;
        setElapsed(ne);
      } else if (e.code === 'ArrowRight') {
        const ne = Math.min(TOTAL_DURATION, elapsed + 2);
        pausedAtRef.current = ne;
        setElapsed(ne);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [elapsed]);
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F]" data-id="element-1277">
      {/* Slides — only render the active one (with crossfade) for performance */}
      <AnimatePresence mode="wait" data-id="element-1278">
        {slideInfo.index === 0 && <motion.div key="slide-1" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1279">
            <PresentationSlide1 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1280" />
          </motion.div>}
        {slideInfo.index === 1 && <motion.div key="slide-2" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1281">
            <PresentationSlide2 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1282" />
          </motion.div>}
        {slideInfo.index === 2 && <motion.div key="slide-3" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1283">
            <PresentationSlide3 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1284" />
          </motion.div>}
        {slideInfo.index === 3 && <motion.div key="slide-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1285">
            <PresentationSlide4 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1286" />
          </motion.div>}
        {slideInfo.index === 4 && <motion.div key="slide-5" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1287">
            <PresentationSlide5 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1288" />
          </motion.div>}
        {slideInfo.index === 5 && <motion.div key="slide-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1289">
            <PresentationSlide6 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1290" />
          </motion.div>}
        {slideInfo.index === 6 && <motion.div key="slide-7" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1291">
            <PresentationSlide7 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1292" />
          </motion.div>}
        {slideInfo.index === 7 && <motion.div key="slide-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0" data-id="element-1293">
            <PresentationSlide8 externalElapsed={slideInfo.localElapsed} hideChrome data-id="element-1294" />
          </motion.div>}
      </AnimatePresence>

      {/* TOP-LEFT: VEXU brand */}
      <div className="absolute top-6 left-6 z-[200] flex items-center gap-2.5 pointer-events-none" data-id="element-1295">
        <img src="https://cdn.magicpatterns.com/uploads/oXaGv3T4d9RhEBXru3pAyj/vexu_logo.svg" alt="VEXU" className="w-8 h-8" style={{
        filter: 'drop-shadow(0 2px 8px rgba(16,217,106,0.35))'
      }} data-id="element-1296" />
        <div className="text-sm font-semibold tracking-tight text-white/85" data-id="element-1297">
          Vexu.ai
        </div>
      </div>

      {/* TOP-RIGHT: Play/Pause + Replay */}
      <div className="absolute top-6 right-6 z-[200] flex items-center gap-2" data-id="element-1298">
        <button onClick={() => {
        if (!playing && elapsed >= TOTAL_DURATION - 0.05) {
          // At end — restart from 0
          pausedAtRef.current = 0;
          startedAtRef.current = Date.now();
          setElapsed(0);
          setPlaying(true);
        } else {
          setPlaying(p => !p);
        }
      }} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" aria-label={playing ? 'Pause' : 'Play'} data-id="element-1299">
          {playing ? <>
              <Pause className="w-3.5 h-3.5" data-id="element-1300" /> Pause
            </> : <>
              <Play className="w-3.5 h-3.5" data-id="element-1301" /> Play
            </>}
        </button>
        <button onClick={replay} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/85 hover:text-white transition-all" aria-label="Replay" data-id="element-1302">
          <RotateCcw className="w-3.5 h-3.5" data-id="element-1303" /> Replay
        </button>
      </div>

      {/* BOTTOM: Slide nav + draggable timeline */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[200] w-[min(96vw,1180px)] px-4" data-id="element-1304">
        {/* Slide labels + nav */}
        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-1305">
          <button onClick={() => goToSlide(Math.max(0, slideInfo.index - 1))} disabled={slideInfo.index === 0} className="flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold tracking-[0.18em] uppercase text-white/55 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0" data-id="element-1306">
            <ChevronLeft className="w-3 h-3" data-id="element-1307" /> Prev
          </button>

          <div className="flex items-center gap-1 flex-1 justify-center min-w-0" data-id="element-1308">
            {SLIDES.map((s, i) => <button key={s.id} onClick={() => goToSlide(i)} className={`text-[9px] font-bold tracking-[0.16em] uppercase px-2.5 py-1 rounded-full transition-all whitespace-nowrap ${slideInfo.index === i ? 'bg-white/[0.08] text-white border border-white/15' : 'text-white/45 hover:text-white/85 border border-transparent'}`} data-id="element-1309">
                <span className="font-mono mr-1 opacity-60" data-id="element-1310">0{i + 1}</span>
                {s.label}
              </button>)}
          </div>

          <button onClick={() => goToSlide(Math.min(SLIDES.length - 1, slideInfo.index + 1))} disabled={slideInfo.index === SLIDES.length - 1} className="flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-bold tracking-[0.18em] uppercase text-white/55 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0" data-id="element-1311">
            Next <ChevronRight className="w-3 h-3" data-id="element-1312" />
          </button>
        </div>

        {/* Draggable timeline */}
        <div ref={trackRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} className="relative h-6 flex items-center cursor-pointer select-none touch-none group" role="slider" aria-valuemin={0} aria-valuemax={TOTAL_DURATION} aria-valuenow={elapsed} aria-label="Presentation timeline" data-id="element-1313">
          {/* Track */}
          <div className="relative w-full h-1.5 rounded-full bg-white/[0.07] overflow-hidden" data-id="element-1314">
            {/* Per-slide segment dividers (visual reference) */}
            {SLIDES.slice(0, -1).map((_, i) => {
            const cumulative = SLIDES.slice(0, i + 1).reduce((a, s) => a + s.duration, 0);
            const left = cumulative / TOTAL_DURATION * 100;
            return <div key={`div-${i}`} className="absolute top-0 bottom-0 w-px bg-white/15" style={{
              left: `${left}%`
            }} data-id="element-1315" />;
          })}
            {/* Played fill */}
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 via-emerald-300 to-amber-300 rounded-full" style={{
            width: `${elapsed / TOTAL_DURATION * 100}%`
          }} data-id="element-1316" />
          </div>

          {/* Scrub handle (plain div for instant, smooth tracking) */}
          <div className="absolute top-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_16px_rgba(255,255,255,0.6)] border-2 border-emerald-300 pointer-events-none transition-transform duration-150" style={{
          left: `${elapsed / TOTAL_DURATION * 100}%`,
          transform: `translate(-50%, -50%) scale(${scrubbing ? 1.25 : 1})`
        }} data-id="element-1317" />
        </div>

        {/* Time + hint */}
        <div className="flex items-center justify-between mt-2 text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-1318">
          <span className="font-mono" data-id="element-1319">
            {fmt(elapsed)}{' '}
            <span className="text-white/25" data-id="element-1320">/ {fmt(TOTAL_DURATION)}</span>
          </span>
          <span className="hidden md:inline" data-id="element-1321">
            Drag the bar · Space to play/pause · ← → to scrub
          </span>
          <span className="font-mono" data-id="element-1322">
            Slide {String(slideInfo.index + 1).padStart(2, '0')} /{' '}
            {String(SLIDES.length).padStart(2, '0')}
          </span>
        </div>
      </div>
    </div>;
}